/** Console v0.5 Super admin: session A's routes (#378, #379; contracts s.13-14). A separate login and session that
 * works only on /api/admin/* and /api/me; every customer route refuses it. It sees accounts, limits, people's names
 * and emails, and (with B6) organisation names and ABNs -- never ledgers, items, meetings or documents. */

import { apiRequest } from "../api.js";
import type { AbnFound, AbnNotFound, PlanBody } from "./admin-logic.js";
import type { Delivery, Invite } from "./team-logic.js";

export type PlanStatus = "pending_payment" | "active" | "suspended";
export type Preset = "small" | "medium" | "large";

export interface AdminAccount {
  account_id: string;
  name: string;
  type: "practice" | "company";
  plan: { preset: Preset | string; status: PlanStatus };
  limits: { organisations: { used: number; max: number }; users: { used: number; max: number } };
  created_at: string;
  abn?: string | null;
}

export interface AdminPerson {
  user_id: string;
  display_name: string | null;
  email: string | null;
  capabilities: string[];
  status: string;
}

export function fetchAccounts(): Promise<{ accounts: AdminAccount[] }> {
  return apiRequest("GET", "/api/admin/accounts", "Preekie couldn't load the accounts.");
}

export function fetchAccountPeople(accountId: string): Promise<{ people: AdminPerson[] }> {
  return apiRequest("GET", `/api/admin/accounts/${encodeURIComponent(accountId)}/people`, "Preekie couldn't load this account's people.");
}

/** Names and ABNs only (B6). Until it exists the server answers 404, and the screen shows the counts alone. */
export function fetchAccountOrganisations(accountId: string): Promise<{ organisations: Array<{ name: string; abn: string | null }> }> {
  return apiRequest("GET", `/api/admin/accounts/${encodeURIComponent(accountId)}/organisations`, "Preekie couldn't load this account's organisations.");
}

export function changeLimits(accountId: string, maxOrganisations: number, maxUsers: number): Promise<{ limits: AdminAccount["limits"] }> {
  return apiRequest("POST", `/api/admin/accounts/${encodeURIComponent(accountId)}/limits`, "The limits weren't changed.", {
    max_organisations: maxOrganisations,
    max_users: maxUsers,
  });
}

/** BR-0034: the console's own account when it exists with no plan row (it predates plans). At most one entry; the server looks at no other account. */
export interface AccountWithoutPlan {
  account_id: string;
  name: string;
  status: "no plan";
}

export function fetchAccountsWithoutPlan(): Promise<{ accounts: AccountWithoutPlan[] }> {
  return apiRequest("GET", "/api/admin/accounts-without-plan", "Preekie couldn't check for accounts without a plan.");
}

/** The ordinary limits route, naming the plan: for an account with no plan row it creates the row (server: 200 with created: true). */
export function createPlan(accountId: string, body: PlanBody): Promise<{ limits: AdminAccount["limits"]; created?: boolean }> {
  return apiRequest("POST", `/api/admin/accounts/${encodeURIComponent(accountId)}/limits`, "The plan wasn't saved.", body);
}

/** BR-0037: ABN Lookup through the server (super-admin session only). Advice for the create-account form; never blocks it. */
export function lookupAbn(number: string): Promise<AbnFound | AbnNotFound> {
  return apiRequest("GET", `/api/admin/abn-lookup?number=${encodeURIComponent(number)}`, "Preekie couldn't check the ABN.");
}

export interface CreateAccount {
  name: string;
  type: "practice" | "company";
  preset: Preset;
  abn?: string;
  max_organisations?: number;
  max_users?: number;
  admin_email: string;
}

export interface CreatedAccount {
  account: AdminAccount;
  invite: Invite | null;
  delivery: Delivery;
  test_accept_url?: string;
  message?: string;
}

export function createAccount(body: CreateAccount): Promise<CreatedAccount> {
  return apiRequest("POST", "/api/admin/accounts", "The account wasn't created.", body);
}

export function resendAdminInvite(accountId: string, adminEmail?: string): Promise<{ invite: Invite | null; delivery: Delivery; test_accept_url?: string }> {
  return apiRequest(
    "POST",
    `/api/admin/accounts/${encodeURIComponent(accountId)}/resend-admin-invite`,
    "The admin invite wasn't sent.",
    adminEmail ? { admin_email: adminEmail } : {}
  );
}

export function setSuspended(accountId: string, suspend: boolean): Promise<{ plan: { status: PlanStatus } }> {
  return apiRequest(
    "POST",
    `/api/admin/accounts/${encodeURIComponent(accountId)}/${suspend ? "suspend" : "reactivate"}`,
    suspend ? "The account wasn't suspended." : "The account wasn't reactivated."
  );
}

export function adminSignOut(): Promise<unknown> {
  return apiRequest("POST", "/api/admin/logout", "Signing out didn't finish.");
}
